/**
 * The client's only route to the server. Cookies ride along automatically;
 * the client holds no credentials of any kind (invariant 2).
 */

import type {
	AppState,
	ApiErrorBody,
	DictationResponse,
	JobResponse,
	ProgressResponse,
	SessionView,
	SettingsResponse,
	SubmitResponse,
	TurnHistoryEntry,
	TurnResponse,
} from "./types.ts";

export class ApiError extends Error {
	readonly code: string;
	readonly status: number;
	readonly retryable: boolean;

	constructor(status: number, body: ApiErrorBody | null, fallback: string) {
		super(body?.error.message ?? fallback);
		this.name = "ApiError";
		this.status = status;
		this.code = body?.error.code ?? "UNKNOWN";
		this.retryable = body?.error.retryable ?? false;
	}
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
	let response: Response;
	try {
		response = await fetch(path, {
			credentials: "same-origin",
			headers: init.body === undefined ? {} : { "content-type": "application/json" },
			...init,
		});
	} catch (cause) {
		throw new ApiError(0, null, "No connection. This will retry when you are back online.");
	}

	if (!response.ok) {
		let body: ApiErrorBody | null = null;
		try {
			body = (await response.json()) as ApiErrorBody;
		} catch {
			body = null;
		}
		throw new ApiError(response.status, body, `Request failed with ${response.status}.`);
	}

	if (response.status === 204) return undefined as T;
	return (await response.json()) as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
	request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

export const api = {
	checkAuth: (): Promise<{ authenticated: boolean }> => request("/api/auth"),
	login: (password: string): Promise<{ ok: boolean }> => post("/api/login", { password }),
	logout: (): Promise<{ ok: boolean }> => post("/api/logout"),

	state: (): Promise<AppState> => request("/api/state"),
	session: (id: number): Promise<SessionView> => request(`/api/sessions/${id}`),
	start: (id: number): Promise<{ id: number; state: string }> => post(`/api/sessions/${id}/start`),

	turn: (
		id: number,
		body: { block_id: number; text: string; round: number; history: TurnHistoryEntry[] },
	): Promise<TurnResponse> => post(`/api/sessions/${id}/turn`, body),

	/** Idempotent: replaying it keeps the first completion timestamp. */
	completeBlocks: (id: number, blockIds: number[]): Promise<{ completed: number[] }> =>
		post(`/api/sessions/${id}/progress`, { block_ids: blockIds }),

	dictation: (id: number, body: { block_id: number; text: string }): Promise<DictationResponse> =>
		post(`/api/sessions/${id}/dictation`, body),

	submit: (
		id: number,
		answers: { id: string; block_id: number; text: string }[],
	): Promise<SubmitResponse | { grading_job_id: string; state: string }> =>
		post(`/api/sessions/${id}/submit`, { answers }),

	job: (id: string): Promise<JobResponse> => request(`/api/jobs/${id}`),
	retryPlan: (): Promise<{ job_id: string | null; message?: string }> => post("/api/plan/retry"),

	progress: (): Promise<ProgressResponse> => request("/api/progress"),
	settings: (): Promise<SettingsResponse> => request("/api/settings"),
	saveSettings: (body: { interests?: string[]; target_minutes?: number }): Promise<unknown> =>
		post("/api/settings", body),

	/** Irreversible. The server refuses without the exact phrase. */
	resetProgress: (): Promise<{ reset: boolean }> =>
		post("/api/settings/reset", { confirm: "reset my progress" }),

	attempts: (attempts: unknown[]): Promise<{ results: { id: string; status: string }[] }> =>
		post("/api/attempts", { attempts }),
};
